import {
  ModalActionsStyled,
  ModalStyled,
  ListStyled,
  TextStyled,
  toggleSiblingNodes,
} from '@nike/nike-design-system-components';
import { isSome, match, Default } from '@nike/rcf-fp';
import Big from 'big.js';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {
  useTillNumber,
  useTenders,
  useSubmitError,
  useSubmitSuccess,
  useSubmitLoading,
  useSubmitDisabled,
} from './hooks/PickupProvider';
import { useGetLocalization } from '../../../../hooks';
import {
  ID_MODAL_ROOT,
  ID_MODAL_SUBMIT_BUTTON,
  ID_OPEN_MODAL_SUBMIT_BUTTON,
} from '../../../../utils/dom-element-ids';
import { ButtonSubmit, MultipleCurrencyText } from '../../../reusable';

const emptyString = ' ';

const getLocalVarianceReason = (varianceReason) =>
  match(varianceReason)(
    ['Short', () => 'lbl.variance-short'],
    ['Over', () => 'lbl.variance-over'],
    ['Negative Till Balance', () => 'lbl.variance-negative-till-balance'],
    [Default, () => 'lbl.no-variance'],
  );

function PickupDeclarationModal({ modalTitle, submit, success }) {
  const { tillNumber } = useTillNumber();
  const { tenders } = useTenders();
  const { submitError } = useSubmitError();
  const { submitSuccess } = useSubmitSuccess();
  const { submitLoading } = useSubmitLoading();
  const { submitDisabled } = useSubmitDisabled();
  const isDisabled = !success || submitDisabled || submitLoading;
  const getLocalization = useGetLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const tenderPickupsThatNeedConfirmation = tenders.filter((tender) => tender.isEditable);

  const openModal = () => {
    setIsOpen(true);
    toggleSiblingNodes(`#${ID_MODAL_ROOT}`, true, undefined);
  };

  const closeModal = () => {
    setIsOpen(false);
    toggleSiblingNodes(`#${ID_MODAL_ROOT}`, false, undefined);
  };

  const actions = (
    <ModalActionsStyled>
      <ButtonSubmit
        appearance="primary"
        ariaLabel="close-dialog"
        buttonSize="small"
        isSubmitting={submitLoading}
        label={getLocalization('lbl.submit')}
        messageError={submitError}
        messageSuccess={submitError ? emptyString : submitSuccess}
        onSubmit={submit}
      />
    </ModalActionsStyled>
  );

  // Helper function to prettify tenderType
  const prettifyTenderType = (type) => {
    if (!type) return '';
    return type
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  };

  const tenderConfirmation = tenderPickupsThatNeedConfirmation.map((tender) => {
    const { tenderType, subTenderType, primaryCurrency, varianceReason, varianceExplanation } =
      tender;

    if (!primaryCurrency)
      return (
        <li className="ncss-row">
          <TextStyled className="headline-4 ncss-col-sm-6 full">
            {getLocalization('lbl.total-sub-tender-type-counted', {
              SUBTENDERTYPE: subTenderType || prettifyTenderType(tenderType),
            })}
            :
          </TextStyled>
          <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
            <MultipleCurrencyText
              value={tender.actualAmount}
              isForeignTender={tender.isForeignTender}
              foreignCurrency={subTenderType}
            />
          </TextStyled>
          <ListStyled className="ncss-container flx-dir-*-c" listStyle="none">
            <li className="ncss-row">
              <TextStyled className="headline-4 ncss-col-sm-6 full">
                {getLocalization('lbl.variance-reason')}:{' '}
              </TextStyled>
              <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
                {getLocalization(getLocalVarianceReason(varianceReason))}
              </TextStyled>
              <TextStyled className="headline-4 ncss-col-sm-6 full">
                {getLocalization('lbl.variance-explanation')}:{' '}
              </TextStyled>
              <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
                {varianceExplanation}
              </TextStyled>
            </li>
          </ListStyled>
        </li>
      );

    const { acceptedLoanAmount } = tender;
    const isAcceptedLoan = isSome(acceptedLoanAmount) && acceptedLoanAmount > 0;
    const acceptedLoan = new Big(acceptedLoanAmount).toFixed(2);
    const actualAmount = new Big(tender.actualAmount).toFixed(2);
    const bankDepositContribution = isAcceptedLoan
      ? new Big(tender.actualAmount - acceptedLoanAmount).toFixed(2)
      : new Big(tender.actualAmount).toFixed(2);
    const defaultFloat = new Big(tender.defaultFloat).toFixed(2);
    return (
      <li className="ncss-row">
        <TextStyled className="headline-4 ncss-col-sm-6 full">
          {`Total ${subTenderType} Counted:`}
        </TextStyled>
        <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
          <MultipleCurrencyText value={actualAmount} />
        </TextStyled>
        <ListStyled className="ncss-container flx-dir-*-c" listStyle="none">
          <li className="ncss-row">
            {isAcceptedLoan && (
              <>
                <TextStyled className="headline-4 ncss-col-sm-6 full">
                  {getLocalization('lbl.loan-amount-to-return-to-safe')}:
                </TextStyled>
                <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
                  <MultipleCurrencyText value={acceptedLoan} />
                </TextStyled>
              </>
            )}
            <TextStyled className="headline-4 ncss-col-sm-6 full">
              {getLocalization('lbl.bank-deposit-contribution')}:
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
              <MultipleCurrencyText value={bankDepositContribution} />
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full">
              {getLocalization('lbl.closing-float')}:{' '}
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
              <MultipleCurrencyText value={defaultFloat} />
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full">
              {getLocalization('lbl.variance-reason')}:{' '}
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
              {getLocalization(getLocalVarianceReason(varianceReason))}
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full">
              {getLocalization('lbl.variance-explanation')}:{' '}
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">
              {varianceExplanation}
            </TextStyled>
          </li>
        </ListStyled>
      </li>
    );
  });

  return (
    <>
      <ButtonSubmit
        id={ID_OPEN_MODAL_SUBMIT_BUTTON}
        isDisabled={isDisabled}
        label={getLocalization('lbl.confirm-final-pickup-amounts')}
        messageError={submitError}
        messageSuccess={submitError ? emptyString : submitSuccess}
        size="large"
        onSubmit={openModal}
      />
      <ModalStyled
        closeModal={closeModal}
        footerContent={actions}
        isOpen={isOpen}
        title={modalTitle}
      >
        <ListStyled className="ncss-container flx-dir-*-c" listStyle="none">
          <li className="ncss-row">
            <TextStyled className="headline-4 ncss-col-sm-6 full">
              {getLocalization('lbl.till-number')}:
            </TextStyled>
            <TextStyled className="headline-4 ncss-col-sm-6 full ta-sm-r">{tillNumber}</TextStyled>
          </li>
          {tenderConfirmation}
        </ListStyled>
        <ButtonSubmit
          id={ID_MODAL_SUBMIT_BUTTON}
          isDisabled={isDisabled}
          isSubmitting={submitLoading}
          label={getLocalization('lbl.submit')}
          size="large"
          onSubmit={() => {
            submit();
            closeModal();
          }}
        />
      </ModalStyled>
    </>
  );
}

PickupDeclarationModal.propTypes = {
  modalTitle: PropTypes.string.isRequired,
  submit: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
};

export default PickupDeclarationModal;
